import React, { FC, useCallback, useContext, useEffect, useState } from 'react'
import { NavLink } from 'react-router-dom'
import {
  AppBar,
  Box,
  Button,
  Grid,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  MenuProps,
  Toolbar,
} from '@mui/material'
import { Theme } from '@mui/material/styles'
import { makeStyles, withStyles } from '@mui/styles'
import { toast } from 'react-toastify'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import { ReactComponent as Logout } from '../../assets/icons/logout.svg'
import { routes } from '../../constants/routes'
import { AppStateContext, useAuth, useRest, useUserName, useUserRole, useLastLocation } from '../Context'
import { MenuTabs } from '../MenuTabs'
import { ReactComponent as LogoImg } from '../../images/login/logo.svg'
import { ReactComponent as AccountImg } from '../../images/header/account_circle.svg'

const useStyles = makeStyles((theme: Theme) => ({
  appBar: {
    width: '100%',
    height: 56,
    backgroundColor: theme.palette.info.dark,
    boxShadow: '0 4px 6px 0 rgba(0,0,0,0.1)',
    stroke: 'unset !important',
    '& .MuiToolbar-root': {
      display: 'flex',
      justifyContent: 'space-between',
      flexDirection: 'row',
      alignItems: 'center',
      width: '100%',
      minHeight: 56,
      margin: '0 auto',
      padding: '0 32px',
      boxSizing: 'border-box',
      '& > a > svg': {
        display: 'block',
        width: 129,
        maxWidth: '100%',
        height: 'auto',
        verticalAlign: 'middle',
      },
      '& .MuiBox-root': {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        '& button': {
          color: theme.palette.primary.light,
          opacity: '0.87',
          fontSize: 14,
          letterSpacing: 0,
          textTransform: 'capitalize',
          justifyContent: 'space-between',
          '& > svg': {
            opacity: '0.6',
            marginRight: '10px',
          },
          '& svg.MuiSvgIcon-root': {
            opacity: '0.6',
            marginLeft: '10px',
            marginRight: 0,
          },
        },
      },
      '& + .MuiGrid-container': {
        position: 'relative',
        // maxWidth: 1440,
        margin: '0 auto',
      },
    },
  },
  shape: {
    shapeRendering: 'auto',
  },
}))

const StyledMenu = withStyles({
  paper: {
    border: '1px solid #d3d4d5',
  },
})((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: 'bottom',
      horizontal: 'center',
    }}
    transformOrigin={{
      vertical: 'top',
      horizontal: 'center',
    }}
    {...props}
  />
))

const StyledMenuItem = withStyles((theme: Theme) => ({
  root: {
    backgroundColor: 'transparent',
    '&:focus': {
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: theme.palette.common.white,
      },
    },
  },
}))(MenuItem)

export const Header: FC = () => {
  const classes = useStyles()
  const { removeUserName } = useUserName()
  const { removeUserRole } = useUserRole()
  const { removeAccessTokenData } = useAuth()
  const { removeLastLocation } = useLastLocation()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [userName, setUserName] = useState<string>()
  const { rest } = useRest()
  const { removeAuthAccessToken } = useAuth()

  const { getUserName } = useContext(AppStateContext)

  useEffect(() => {
    setUserName(getUserName)
  }, [getUserName, setUserName])

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget)
    },
    [setAnchorEl],
  )

  const handleClose = useCallback(() => {
    setAnchorEl(null)
  }, [setAnchorEl])

  const logOutHandler = useCallback(async () => {
    try {
      const {
        data: { status },
      } = await rest({
        method: 'POST',
        url: 'logout',
        withCredentials: true,
      })

      if (status === 'success') {
        removeAccessTokenData()
        removeAuthAccessToken()
        removeUserName()
        removeUserRole()
        removeLastLocation()
        toast.success('Logout is successful')
      }
    } catch (err) {
      // eslint-disable-next-line
      console.log(err)
    }
  }, [removeAccessTokenData, removeAuthAccessToken, removeLastLocation, removeUserName, removeUserRole, rest])

  return (
    <AppBar className={classes.appBar}>
      <Toolbar className={classes.shape}>
        <NavLink exact to={routes.HOME}>
          <LogoImg />
        </NavLink>

        <Box>
          <Button
            aria-controls="customized-menu"
            aria-haspopup="true"
            variant="text"
            color="primary"
            onClick={handleClick}
          >
            <AccountImg />
            {userName}
            {anchorEl ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </Button>

          <StyledMenu
            id="customized-menu"
            anchorEl={anchorEl}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <StyledMenuItem onClick={logOutHandler}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </StyledMenuItem>
          </StyledMenu>
        </Box>
      </Toolbar>
      <Grid container>
        <MenuTabs />
      </Grid>
    </AppBar>
  )
}
